// src/pages/CelebritiesBrowsePage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom'; // Import useNavigate and useSearchParams
import apiService from '../services/api';
import CopyableId from '../components/common/CopyableId';
import PaginationControls from '../components/common/PaginationControls'; // Import the new component

const DEFAULT_LIMIT = 20;

const CelebritiesBrowsePage = () => {
  const [celebrities, setCelebrities] = useState([]);
  const [pagination, setPagination] = useState({
    limit: DEFAULT_LIMIT,
    page: 1,
    sort_by: 'lastName',
    sort_order: 'ASC',
    total: 0,
    total_pages: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate(); // Initialize navigate hook
  const [searchParams, setSearchParams] = useSearchParams(); // Initialize useSearchParams
  const [searchTerm, setSearchTerm] = useState(searchParams.get('query') || ''); // Initialize searchTerm from URL
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm); // State for debounced search term
  const containerRef = useRef(null); // Ref for the main container
  const paginationRef = useRef(null); // Ref for the pagination controls

  // Function to fetch celebrities
  const fetchCelebrities = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sort_by: pagination.sort_by,
        sort_order: pagination.sort_order,
        query: debouncedSearchTerm, // Pass the debounced search term
      };
      const response = await apiService.browseCelebrities(params);
      setCelebrities(response.data || []);
      setPagination(response.pagination || { ...pagination, total: 0, total_pages: 0 });
    } catch (err) {
      console.error("Error fetching celebrities:", err);
      setError("Failed to load celebrities. Please try again later.");
      setCelebrities([]); // Clear data on error
      setPagination(prev => ({ ...prev, total: 0, total_pages: 0 })); // Reset pagination totals
    } finally {
      setIsLoading(false);
    }
  }, [pagination, debouncedSearchTerm]); // Add debouncedSearchTerm to dependencies

  // Effect to update debouncedSearchTerm after a delay
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Fetch data on initial load and when pagination/sorting/debouncedSearchTerm changes
  useEffect(() => {
    fetchCelebrities();
    // Update URL with search term
    if (searchTerm) {
      searchParams.set('query', searchTerm);
    } else {
      searchParams.delete('query');
    }
    setSearchParams(searchParams);
  }, [fetchCelebrities, searchTerm, searchParams, setSearchParams]); // Add searchTerm and searchParams to dependencies

  // --- Event Handlers ---

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
      // Optional: Scroll table container to top when page changes
      const tableContainer = containerRef.current?.querySelector('.overflow-y-auto');
      if (tableContainer) {
        tableContainer.scrollTop = 0;
      }
    }
  };

  const handleSort = (newSortBy) => {
    const newSortOrder = (pagination.sort_by === newSortBy && pagination.sort_order === 'ASC') ? 'DESC' : 'ASC';
    setPagination(prev => ({
      ...prev,
      sort_by: newSortBy,
      sort_order: newSortOrder,
      page: 1, // Reset to page 1 when sorting changes
    }));
  };

  // Function to render sort indicators
  const renderSortIndicator = (columnName) => {
    if (pagination.sort_by === columnName) {
      return pagination.sort_order === 'ASC' ? ' ▲' : ' ▼';
    }
    return null;
  };

  // Function to handle clicking on a celebrity row (optional navigation)
  const handleCelebrityClick = (personId) => {
    navigate(`/celebrities/${personId}`); // Navigate to the celebrity details page
  };

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // --- Rendering ---

  return (
    // Add flexbox classes for layout and height calculation
    <div className="p-6 flex flex-col h-[calc(100vh-theme(space.16))]" ref={containerRef}>
      <h1 className="text-2xl font-semibold mb-4 shrink-0">Celebrity Search</h1> {/* Changed title */}

      {/* Search Input */}
      <div className="mb-4 shrink-0 flex items-center">
        <input
          type="text"
          className="flex-grow px-3 py-2 border border-gray-300 rounded-l text-sm"
          placeholder="Search by name..."
          value={searchTerm}
          onChange={handleSearchInputChange}
        />
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-r text-sm"
          onClick={() => setDebouncedSearchTerm(searchTerm)} // Trigger search on button click
        >
          Search
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        // Make loading/error/empty states grow to fill space
        <div className="flex justify-center items-center flex-grow">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 shrink-0" role="alert">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> {error}</span>
          <button
            onClick={fetchCelebrities}
            className="ml-4 bg-red-200 hover:bg-red-300 text-red-800 py-1 px-2 rounded text-sm"
          >
            Retry
          </button>
        </div>
      )}

      {/* Data Table & Pagination Container */}
      {!isLoading && !error && celebrities.length > 0 && (
        // This container will hold the table (scrollable) and pagination (fixed at bottom)
        <div className="flex flex-col flex-grow overflow-hidden">
           {/* Results Info */}
           <div className="mb-2 text-sm text-gray-600 shrink-0">
              Page {pagination.page} of {pagination.total_pages} ({pagination.total} total celebrities)
            </div>
           {/* Make table container scrollable */}
           <div className="overflow-y-auto flex-grow shadow-md rounded-lg mb-4">
             <table className="min-w-full divide-y divide-gray-200">
               {/* Sticky Header */}
               <thead className="bg-gray-50 sticky top-0 z-10">
                 <tr>
                   <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16"> {/* Added width */}
                     Image
                   </th>
                   <th
                     scope="col"
                     className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                     onClick={() => handleSort('lastName')}
                   >
                     Name {renderSortIndicator('lastName')}
                   </th>
                   <th
                     scope="col"
                     className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                     onClick={() => handleSort('personId')}
                   >
                     Person ID {renderSortIndicator('personId')}
                   </th>
                 </tr>
               </thead>
               <tbody className="bg-white divide-y divide-gray-200">
                   {celebrities.map((celeb) => (
                     <tr key={celeb.personId} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleCelebrityClick(celeb.personId)}>
                       <td className="px-4 py-2 whitespace-nowrap">
                         {/* Adjusted image size and centering */}
                         <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center">
                           {celeb.imageUrl ? (
                             <img className="h-12 w-12 rounded-full object-cover" src={celeb.imageUrl} alt={celeb.fullName || ''} />
                           ) : (
                             <span className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">
                               <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                               </svg>
                             </span>
                           )}
                         </div>
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                         {celeb.fullName || `${celeb.firstName || ''} ${celeb.lastName || ''}`.trim()}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <CopyableId label="ID" value={celeb.personId.toString()} />
                       </td>
                     </tr>
                   ))}
               </tbody>
             </table>
           </div> {/* End scrollable table container */}

           {/* Use the new PaginationControls component */}
           <div ref={paginationRef} className="shrink-0"> {/* Added ref and shrink-0 */}
             <PaginationControls
               currentPage={pagination.page}
               totalPages={pagination.total_pages}
               onPageChange={handlePageChange}
             />
           </div>
         </div> // End flex container for table and pagination
       )}

       {/* No Results Message */}
       {!isLoading && !error && celebrities.length === 0 && (
          <div className="text-center text-gray-500 p-4 flex-grow">
             No celebrities found.
          </div>
       )}
     </div> // End main container
   );
 };

 export default CelebritiesBrowsePage;
